/**
 * CRM Login & Register pages — siloed CRM auth.
 * Routes:
 *   /crm/login           → CRMLoginPage
 *   /crm/register        → CRMRegisterChoicePage (personal vs business)
 *   /crm/register/personal
 *   /crm/register/business
 *   /crm/invite/:token   → CRMInviteAcceptPage
 */
import { useState, useEffect } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useCRMAuth } from "@/contexts/CRMAuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { API } from "@/App";
import { User, Building2, ArrowRight, Briefcase, ShieldCheck, Users } from "lucide-react";

const COMPANY_LOGO = "https://customer-assets.emergentagent.com/job_bizcommerce-19/artifacts/7hv4d1j9_LOGO%20PNG.png";

const SEAT_PRICE = 9.99;

const Shell = ({ children, title, subtitle }) => (
  <div className="min-h-screen bg-slate-50 flex flex-col">
    <nav className="bg-slate-900 border-b border-slate-800">
      <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
        <Link to="/crm" className="flex items-center gap-3">
          <img src={COMPANY_LOGO} alt="V" className="h-9 w-auto brightness-0 invert" />
          <div className="h-7 w-px bg-white/30" />
          <span className="text-white font-brand font-bold">VEYRONIX CRM</span>
        </Link>
        <Link to="/crm" className="text-slate-300 hover:text-white text-sm">← Back to CRM</Link>
      </div>
    </nav>
    <div className="flex-1 flex items-center justify-center px-6 py-12">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <h1 className="font-heading text-3xl font-bold text-slate-900 mb-2">{title}</h1>
          {subtitle && <p className="text-slate-500">{subtitle}</p>}
        </div>
        {children}
      </div>
    </div>
  </div>
);

// ============ LOGIN ============
export const CRMLoginPage = () => {
  const navigate = useNavigate();
  const { loginCRM, crmUser } = useCRMAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (crmUser) navigate("/crm-app");
  }, [crmUser, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await loginCRM(email, password);
      toast.success("Welcome back!");
      navigate("/crm-app");
    } catch (err) {
      toast.error(err.message || "Login failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Shell title="Sign in to CRM" subtitle="Access your customer relationship workspace">
      <form onSubmit={handleSubmit} className="bg-white rounded-2xl border border-slate-200 p-8 shadow-sm space-y-5" data-testid="crm-login-form">
        <div>
          <Label htmlFor="email">Email</Label>
          <Input id="email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} data-testid="crm-login-email" />
        </div>
        <div>
          <Label htmlFor="password">Password</Label>
          <Input id="password" type="password" required value={password} onChange={(e) => setPassword(e.target.value)} data-testid="crm-login-password" />
        </div>
        <Button type="submit" disabled={submitting} className="w-full bg-indigo-600 hover:bg-indigo-700 h-11" data-testid="crm-login-submit">
          {submitting ? "Signing in..." : "Sign in"}
        </Button>
        <p className="text-center text-sm text-slate-600">
          Don't have an account? <Link to="/crm/register" className="text-indigo-600 font-medium hover:underline">Get started</Link>
        </p>
      </form>
    </Shell>
  );
};

// ============ REGISTER CHOICE ============
export const CRMRegisterChoicePage = () => {
  const navigate = useNavigate();
  return (
    <Shell title="Choose your CRM plan" subtitle="14-day free trial. £9.99 per seat / month after.">
      <div className="space-y-4">
        <button
          onClick={() => navigate("/crm/register/personal")}
          className="w-full text-left bg-white rounded-2xl border border-slate-200 p-6 hover:border-indigo-300 hover:shadow-lg transition-all group"
          data-testid="choice-personal"
        >
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 rounded-xl bg-indigo-50 border border-indigo-100 flex items-center justify-center">
              <User className="h-6 w-6 text-indigo-600" />
            </div>
            <div className="flex-1">
              <h3 className="font-heading font-bold text-lg text-slate-900">Personal</h3>
              <p className="text-sm text-slate-500 mt-1">For freelancers & solo operators. Single user, full features.</p>
              <p className="text-sm font-medium text-indigo-600 mt-2">£9.99/month after 14-day trial</p>
            </div>
            <ArrowRight className="h-5 w-5 text-slate-400 group-hover:text-indigo-600 group-hover:translate-x-1 transition-all" />
          </div>
        </button>

        <button
          onClick={() => navigate("/crm/register/business")}
          className="w-full text-left bg-gradient-to-br from-indigo-600 to-violet-700 text-white rounded-2xl p-6 hover:shadow-xl transition-all group relative overflow-hidden"
          data-testid="choice-business"
        >
          <div className="absolute top-0 right-0 w-40 h-40 bg-white/10 rounded-full blur-[60px]" />
          <div className="flex items-start gap-4 relative">
            <div className="w-12 h-12 rounded-xl bg-white/15 flex items-center justify-center">
              <Building2 className="h-6 w-6" />
            </div>
            <div className="flex-1">
              <h3 className="font-heading font-bold text-lg">Business</h3>
              <p className="text-sm text-indigo-100 mt-1">For teams. Multiple seats, invite employees, role-based access.</p>
              <p className="text-sm font-semibold mt-2">£9.99 per seat / month • 14-day trial</p>
            </div>
            <ArrowRight className="h-5 w-5 group-hover:translate-x-1 transition-all" />
          </div>
        </button>

        <p className="text-center text-sm text-slate-500 pt-4">
          Already have an account? <Link to="/crm/login" className="text-indigo-600 font-medium hover:underline">Sign in</Link>
        </p>
      </div>
    </Shell>
  );
};

// ============ REGISTER PERSONAL ============
export const CRMRegisterPersonalPage = () => {
  const navigate = useNavigate();
  const { registerPersonal } = useCRMAuth();
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await registerPersonal(form);
      toast.success("Account created — welcome to your 14-day trial!");
      navigate("/crm-app");
    } catch (err) {
      toast.error(err.message || "Registration failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Shell title="Personal CRM" subtitle="Free for 14 days, then £9.99/month. Cancel anytime.">
      <form onSubmit={handleSubmit} className="bg-white rounded-2xl border border-slate-200 p-8 shadow-sm space-y-5" data-testid="personal-register-form">
        <div>
          <Label htmlFor="p-name">Full name</Label>
          <Input id="p-name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="p-name" />
        </div>
        <div>
          <Label htmlFor="p-email">Email</Label>
          <Input id="p-email" type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="p-email" />
        </div>
        <div>
          <Label htmlFor="p-password">Password (min 8 chars)</Label>
          <Input id="p-password" type="password" required minLength={8} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="p-password" />
        </div>
        <Button type="submit" disabled={submitting} className="w-full bg-indigo-600 hover:bg-indigo-700 h-11" data-testid="p-submit">
          {submitting ? "Creating..." : "Start 14-day trial"}
        </Button>
        <p className="text-center text-xs text-slate-500">No credit card required for trial.</p>
        <p className="text-center text-sm text-slate-600">
          <Link to="/crm/register" className="text-indigo-600 hover:underline">← Choose a different plan</Link>
        </p>
      </form>
    </Shell>
  );
};

// ============ REGISTER BUSINESS ============
export const CRMRegisterBusinessPage = () => {
  const navigate = useNavigate();
  const { registerBusiness } = useCRMAuth();
  const [form, setForm] = useState({
    owner_name: "",
    email: "",
    password: "",
    business_name: "",
    business_size: "1-10",
    industry: "",
    seats: 5,
  });
  const [submitting, setSubmitting] = useState(false);
  const monthly = (form.seats * SEAT_PRICE).toFixed(2);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await registerBusiness(form);
      toast.success(`Trial started for ${form.seats} seats! Add payment to continue after 14 days.`);
      navigate("/crm-app");
    } catch (err) {
      toast.error(err.message || "Registration failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Shell title="Business CRM" subtitle={`14-day free trial, then £${SEAT_PRICE}/seat/month`}>
      <form onSubmit={handleSubmit} className="bg-white rounded-2xl border border-slate-200 p-8 shadow-sm space-y-5" data-testid="business-register-form">
        {/* Business info */}
        <div className="space-y-4">
          <div>
            <Label htmlFor="b-name">Business name</Label>
            <Input id="b-name" required value={form.business_name} onChange={(e) => setForm({ ...form, business_name: e.target.value })} data-testid="b-business-name" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label htmlFor="b-size">Team size</Label>
              <select id="b-size" value={form.business_size} onChange={(e) => setForm({ ...form, business_size: e.target.value })} className="mt-2 w-full h-10 rounded-md border border-slate-300 bg-white px-3 text-sm">
                <option value="1-10">1–10</option>
                <option value="11-50">11–50</option>
                <option value="51-200">51–200</option>
                <option value="200+">200+</option>
              </select>
            </div>
            <div>
              <Label htmlFor="b-industry">Industry (optional)</Label>
              <Input id="b-industry" value={form.industry} onChange={(e) => setForm({ ...form, industry: e.target.value })} placeholder="e.g. Retail, SaaS" />
            </div>
          </div>
        </div>

        <div className="border-t border-slate-200 pt-5 space-y-4">
          <h3 className="font-medium text-slate-900 flex items-center gap-2"><Briefcase className="h-4 w-4 text-indigo-600" /> Owner account</h3>
          <div>
            <Label htmlFor="b-owner">Your name</Label>
            <Input id="b-owner" required value={form.owner_name} onChange={(e) => setForm({ ...form, owner_name: e.target.value })} data-testid="b-owner-name" />
          </div>
          <div>
            <Label htmlFor="b-email">Work email</Label>
            <Input id="b-email" type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="b-email" />
          </div>
          <div>
            <Label htmlFor="b-password">Password</Label>
            <Input id="b-password" type="password" required minLength={8} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="b-password" />
          </div>
        </div>

        {/* Seats */}
        <div className="border-t border-slate-200 pt-5 space-y-3">
          <div className="flex items-center justify-between">
            <Label htmlFor="b-seats" className="flex items-center gap-2"><Users className="h-4 w-4 text-indigo-600" /> Number of seats</Label>
            <span className="text-sm font-medium text-indigo-600">{form.seats} seats</span>
          </div>
          <input
            id="b-seats"
            type="range"
            min={1}
            max={50}
            value={form.seats}
            onChange={(e) => setForm({ ...form, seats: parseInt(e.target.value) })}
            className="w-full"
            data-testid="b-seats-slider"
          />
          <div className="flex items-center justify-between text-sm pt-2 border-t border-slate-100">
            <span className="text-slate-600">After trial</span>
            <span className="font-bold text-slate-900">£{monthly}/month</span>
          </div>
        </div>

        <Button type="submit" disabled={submitting} className="w-full bg-indigo-600 hover:bg-indigo-700 h-11" data-testid="b-submit">
          {submitting ? "Creating..." : `Start 14-day trial (${form.seats} seats)`}
        </Button>
        <p className="text-center text-xs text-slate-500">
          <ShieldCheck className="h-3 w-3 inline mr-1" />
          No credit card required. Invite team members after signup.
        </p>
        <p className="text-center text-sm text-slate-600">
          <Link to="/crm/register" className="text-indigo-600 hover:underline">← Choose a different plan</Link>
        </p>
      </form>
    </Shell>
  );
};

// ============ ACCEPT INVITATION ============
export const CRMInviteAcceptPage = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const { acceptInvitation } = useCRMAuth();
  const [invite, setInvite] = useState(null);
  const [error, setError] = useState(null);
  const [form, setForm] = useState({ name: "", password: "" });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    let cancelled = false;
    fetch(`${API}/crm/auth/invitations/${token}`)
      .then((r) => (r.ok ? r.json() : Promise.reject(r)))
      .then((data) => {
        if (cancelled) return;
        if (data.status !== "pending") {
          setError(`Invitation ${data.status}.`);
        } else {
          setInvite(data);
          setForm((f) => ({ ...f, name: data.name || "" }));
        }
      })
      .catch(() => setError("Invitation not found or already used."));
    return () => { cancelled = true; };
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await acceptInvitation({ token, name: form.name, password: form.password });
      toast.success("Welcome to the team!");
      navigate("/crm-app");
    } catch (err) {
      toast.error(err.message || "Failed to accept invitation");
    } finally {
      setSubmitting(false);
    }
  };

  if (error) {
    return (
      <Shell title="Invitation unavailable" subtitle={error}>
        <div className="bg-white rounded-2xl border border-slate-200 p-8 text-center">
          <Link to="/crm/login" className="text-indigo-600 hover:underline">Sign in instead</Link>
        </div>
      </Shell>
    );
  }
  if (!invite) {
    return <Shell title="Loading invitation..."><div className="text-center text-slate-500">One moment...</div></Shell>;
  }

  return (
    <Shell
      title={`Join ${invite.business_name}`}
      subtitle={`${invite.invited_by_name} invited you to ${invite.email}`}
    >
      <form onSubmit={handleSubmit} className="bg-white rounded-2xl border border-slate-200 p-8 shadow-sm space-y-5" data-testid="invite-accept-form">
        <div>
          <Label>Email</Label>
          <Input value={invite.email} disabled className="bg-slate-50" />
        </div>
        <div>
          <Label htmlFor="i-name">Your name</Label>
          <Input id="i-name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="i-name" />
        </div>
        <div>
          <Label htmlFor="i-password">Create password (min 8 chars)</Label>
          <Input id="i-password" type="password" required minLength={8} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="i-password" />
        </div>
        <Button type="submit" disabled={submitting} className="w-full bg-indigo-600 hover:bg-indigo-700 h-11" data-testid="i-submit">
          {submitting ? "Joining..." : `Accept invitation`}
        </Button>
      </form>
    </Shell>
  );
};
